import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { MY_CPM_CAMPAIGN_AUTOMATIC_IMPORT, SET_MY_CPM_CAMPAIGN_AUTOMATIC_IMPORT } from '@/queries/campaignSocialImport';
import { ACCOUNT_CHANGED_EVENT } from '@/lib/axios';
import { canImportCampaign, type ImportCampaign } from '@/lib/campaigns/socialImport';

export type CampaignAutomaticImportOptions = { campaign: ImportCampaign; acceptedTerms: boolean; disabled?: boolean };

/** One inline owner; opening the inventory dialog never reads this preference. */
export function useCampaignAutomaticImport(options: CampaignAutomaticImportOptions) {
  const client = useApolloClient();
  const latest = useRef(options); latest.current = options;
  const identity = useRef({ campaignId: options.campaign.id, token: localStorage.getItem('token') });
  const alive = useRef(true);
  const generation = useRef(0);
  const pending = useRef<number | null>(null);
  const [automaticImport, setAutomaticImport] = useState<boolean | null>(null);
  const [preferenceLoading, setPreferenceLoading] = useState(true);
  const [preferenceSaving, setPreferenceSaving] = useState(false);
  const [preferenceError, setPreferenceError] = useState<string | null>(null);
  const renderVersion = generation.current;
  const eligible = canImportCampaign(options.campaign, options.acceptedTerms);
  const current = useCallback((version = generation.current) => alive.current && version === generation.current
    && identity.current.campaignId === latest.current.campaign.id && identity.current.token === localStorage.getItem('token'), []);

  const loadPreference = useCallback(async () => {
    if (!current() || !canImportCampaign(latest.current.campaign, latest.current.acceptedTerms) || pending.current === generation.current) return;
    const version = generation.current;
    pending.current = version; setPreferenceLoading(true); setAutomaticImport(null); setPreferenceError(null);
    try {
      const response = await client.query({ query: MY_CPM_CAMPAIGN_AUTOMATIC_IMPORT,
        variables: { campaignId: identity.current.campaignId }, fetchPolicy: 'no-cache', errorPolicy: 'none', context: { queryDeduplication: false } });
      if (!current(version)) return;
      const saved = response.data?.myCpmCampaignAutomaticImport;
      if (response.errors?.length || typeof saved !== 'boolean') throw new Error('missing_preference');
      setAutomaticImport(saved);
    } catch {
      if (current(version)) setPreferenceError('campaign.socialImport.preferenceLoadError');
    } finally {
      if (pending.current === version) pending.current = null;
      if (current(version)) setPreferenceLoading(false);
    }
  }, [client, current]);

  const saveAutomaticImport = async (enabled: boolean) => {
    if (!current(renderVersion) || !canImportCampaign(latest.current.campaign, latest.current.acceptedTerms)
      || latest.current.disabled || pending.current === generation.current || automaticImport === null) return;
    const version = generation.current;
    pending.current = version; setPreferenceSaving(true); setPreferenceError(null);
    try {
      const response = await client.mutate({ mutation: SET_MY_CPM_CAMPAIGN_AUTOMATIC_IMPORT,
        variables: { campaignId: identity.current.campaignId, enabled } });
      if (!current(version)) return;
      const saved = response.data?.setMyCpmCampaignAutomaticImport;
      if (response.errors?.length || typeof saved !== 'boolean') throw new Error('missing_preference');
      setAutomaticImport(saved);
    } catch {
      if (current(version)) { setAutomaticImport(null); setPreferenceError('campaign.socialImport.preferenceSaveError'); }
    } finally {
      if (pending.current === version) pending.current = null;
      if (current(version)) setPreferenceSaving(false);
    }
  };

  useEffect(() => {
    alive.current = true;
    const invalidate = () => { generation.current++; };
    const reset = () => {
      identity.current = { campaignId: latest.current.campaign.id, token: localStorage.getItem('token') };
      setAutomaticImport(null); setPreferenceSaving(false); setPreferenceError(null);
      setPreferenceLoading(canImportCampaign(latest.current.campaign, latest.current.acceptedTerms));
      void loadPreference();
    };
    const accountChanged = () => { invalidate(); reset(); };
    window.addEventListener(ACCOUNT_CHANGED_EVENT, accountChanged);
    reset();
    return () => { alive.current = false; invalidate(); window.removeEventListener(ACCOUNT_CHANGED_EVENT, accountChanged); };
  }, [options.campaign.id, eligible, loadPreference]);

  const sessionChanged = !current(renderVersion);
  return { automaticImport: sessionChanged || !eligible ? null : automaticImport, preferenceLoading, preferenceSaving, preferenceError,
    sessionChanged, saveAutomaticImport, retryPreference: loadPreference };
}
